<script lang="ts">
  import { onDestroy, untrack } from 'svelte';
  import { useQueryClient } from '@tanstack/svelte-query';
  import { customerCareApi, type CustomerCallInput } from '$lib/api/customerCare';
  import { getErrorMessage } from '$lib/api/client';
  import type { Issue } from '$lib/types';

  let { issue, onSaved, onCancel }: {
    issue: Pick<Issue, 'id' | 'vertical_id' | 'status' | 'customer_satisfaction' | 'reporter_phone'> | null;
    onSaved: () => void;
    onCancel?: () => void;
  } = $props();
  const client = useQueryClient();
  let generation = 0;
  let identity = '';
  let alive = true;
  let outcome = $state<'' | 'reached' | 'no_answer'>('');
  let body = $state('');
  let satisfaction = $state<CustomerCallInput['customer_satisfaction']>('pending');
  let nextDate = $state('');
  let pending = $state(false);
  let error = $state('');
  let success = $state('');
  const today = () => new Date().toISOString().slice(0, 10);
  $effect(() => {
    const key = issue ? `${issue.id}:${issue.vertical_id}` : '';
    untrack(() => {
      if (key === identity) return;
      identity = key;
      generation++;
      outcome = ''; body = ''; nextDate = ''; error = ''; success = ''; pending = false;
      satisfaction = 'pending';
    });
  });
  onDestroy(() => { alive = false; generation++; });
  async function save(event: SubmitEvent) {
    event.preventDefault();
    const target = issue;
    if (pending || !target) return;
    error = ''; success = '';
    if (!outcome || !body.trim() || body.trim().length > 10000) { error = 'Select the call outcome and enter a summary (up to 10,000 characters).'; return; }
    if ((outcome === 'no_answer' && !nextDate) || (nextDate && nextDate < today())) { error = 'Choose a next call date today or later (UTC). A date is required for no answer.'; return; }
    if (outcome === 'reached' && satisfaction === 'satisfied' && target.status !== 'resolved') { error = 'Only a reached customer on a resolved case can confirm satisfaction.'; return; }
    const id = target.id, team = target.vertical_id, draft = generation;
    const current = () => alive && generation === draft && issue?.id === id && issue?.vertical_id === team;
    const savedCallback = onSaved;
    let notifySaved = false;
    pending = true;
    try {
      await customerCareApi.logCall(id, { vertical_id: team, outcome, body: body.trim(), customer_satisfaction: outcome === 'no_answer' ? '' : satisfaction, next_call_date: nextDate || null });
      // Do not publish a possibly late full response over a newer handover.
      await Promise.all(['customer-care', 'issue-detail', 'issues', 'issues-count', 'issues-board', 'issue-comments', 'dashboard'].map((key) =>
        client.invalidateQueries({ queryKey: key === 'issue-detail' || key === 'issue-comments' ? [key, id] : [key] })));
      if (current()) { success = 'Customer call saved.'; body = ''; notifySaved = true; }
    } catch (err) {
      if (current()) error = getErrorMessage(err);
    } finally { if (current()) pending = false; }
    // The callback may synchronously clear the parent prop. Finish all draft
    // reads and cleanup first; never inspect issue after invoking it.
    if (notifySaved && current()) savedCallback();
  }
</script>

<form class="space-y-3" onsubmit={save}>
  {#if issue?.reporter_phone}<a class="text-sm text-toyama-teal underline" href="tel:{issue.reporter_phone}">{issue.reporter_phone}</a>{/if}
  <fieldset class="space-y-3" disabled={pending}>
    <div><label class="label" for="customer-call-outcome">Call outcome (required)</label>
      <select id="customer-call-outcome" class="input" bind:value={outcome} required>
        <option value="" disabled>Select outcome</option><option value="reached">Reached customer</option><option value="no_answer">No answer</option>
      </select></div>
    <div><label class="label" for="customer-call-summary">Call summary (required)</label>
      <textarea id="customer-call-summary" class="input" rows="3" maxlength="10000" required bind:value={body}></textarea></div>
    {#if outcome === 'reached'}
    <div><label class="label" for="customer-call-satisfaction">Customer satisfaction</label>
      <select id="customer-call-satisfaction" class="input" bind:value={satisfaction}>
        <option value="">Not recorded</option><option value="pending">Pending confirmation</option>{#if outcome === 'reached'}<option value="not_satisfied">Not satisfied</option>{/if}
        {#if outcome === 'reached' && issue?.status === 'resolved'}<option value="satisfied">Satisfied</option>{/if}
      </select></div>
    {:else if outcome === 'no_answer'}
      <p class="text-xs text-dark-gray">No answer does not change existing customer feedback or satisfaction.</p>
    {/if}
    <div><label class="label" for="customer-call-date">Next customer call date (UTC){outcome === 'no_answer' ? ' — required' : ''}</label>
      <input id="customer-call-date" class="input" type="date" bind:value={nextDate} required={outcome === 'no_answer'} min={today()} /></div>
    <p class="text-xs text-dark-gray">Only “Reached customer” records actual customer contact. No answer requires a retry date. Pending or not satisfied without a date stays in the callback queue; a future date defers the callback. This never resolves a case or replaces CAPA.</p>
    <div class="flex gap-2"><button type="submit" class="btn-primary">{pending ? 'Saving…' : 'Save customer call'}</button>
      {#if onCancel}<button type="button" class="btn-secondary" onclick={onCancel}>Cancel</button>{/if}</div>
  </fieldset>
  {#if error}<p class="text-sm text-red-600" role="alert">{error}</p>{/if}
  <p class="text-sm text-toyama-teal" role="status">{pending ? 'Saving customer call…' : success}</p>
</form>
